import { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { SubmitHandler, useFieldArray } from 'react-hook-form'
import toast, { Toaster } from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import Swal, { SweetAlertResult } from 'sweetalert2'
import { FileDropZone } from '../../../../components/forms/dropzone/fileDropZone.type'
import { CargaInfo } from '../../../../components/layout/CargaInfo'
import useHasPermiso from '../../../../hooks/autenticacion/useHasPermiso'
import { Reporte } from '../../../../models'
import { RootState } from '../../../../redux'
import {
  useActualizarCondicionanteMutation,
  useGuardarCondicionanteMutation,
  useRecuperarCondicionanteporIdQuery,
  useRecuperarTareasPorIdRelacionadoMutation,
} from '../../../../redux/api'
import {
  useGuardarReportesMutation,
  useRecuperarReportesPorIdRelacionadoMutation,
} from '../../../../redux/api/reportesCumplimiento.api'
import { cargarReportes } from '../../../../redux/slices/reportes'
import { cargarTareas } from '../../../../redux/slices/tareas'
import { AppConstantes } from '../../../../utils/Constantes'
import dayjs from '../../../../utils/DayjsUtil'
import { Roles } from '../../../../utils/Roles'
import { alertUsuarios } from '../../../../utils/alerts/AlertUsuarios'
import { Recordatorio } from '../../../recordatorios/models'
import { TRecordatorioForm } from '../../../recordatorios/models/recordatorioForm.type'
import { Tarea } from '../../../tareas/models'
import { Condicionante } from '../../models'
import {
  TCondicionanteForm,
  style,
  useCondicionanteForm,
} from '../../models/condicionanteForm.type'
import { ModalCondicionanteBody } from './ModalCondicionanteBody'
import { ModalCondicionanteFooter } from './ModalCondicionanteFooter'
import { ModalCondicionanteHeader } from './ModalCondicionanteHeader'

type Props = {
  idAutorizacion: string | undefined
  idSitio: string | undefined
  titulo: string | undefined
  fechaVencimientoAutorizacion: string | undefined
  unidadAutorizacion: string | undefined
  condicionante?: Condicionante
  isModoEditar: boolean
  show: boolean
  close: () => void
  refetch: () => void
  idSeleccionado: string
  setIsSuccessGuardar: (arg: boolean) => void
  setIsSuccessActualizar: (arg: boolean) => void
}

export const ModalEditarAgregarCondicionante = ({
  idAutorizacion,
  idSitio,
  titulo,
  condicionante,
  fechaVencimientoAutorizacion,
  isModoEditar,
  close,
  show,
  refetch,
  idSeleccionado,
  setIsSuccessActualizar,
  setIsSuccessGuardar,
}: Props) => {
  const { hasPermiso } = useHasPermiso()
  // Permiso de crear condicionantes gobierna edicion en este modal (overlay/acciones).
  const usuarioPermiso = hasPermiso([Roles.roleCondicionantesCrear])

  // Modal de alta/edicion de condicionantes.
  // Incluye: datos generales + tareas + recordatorios (por tarea y recurrentes) + evidencia documental.
  // Nota: este modal persiste la condicionante (guardar/actualizar) y, como paso separado,
  // guarda evidencia documental via FormData; si falla evidencia, la condicionante queda guardada.

  const formCondicionante = useCondicionanteForm()
  const {
    register,
    formState: { errors },
    control,
    reset,
    resetField,
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
  } = formCondicionante

  const { fields, append, remove } = useFieldArray({
    name: 'tareas',
    control,
  })
  const tareasRecuperadas = useSelector((state: RootState) => state.listaTareas)

  const isModoEditarModal = !!condicionante?.idCondicionante

  const [condicionanteCargada, setCondicionanteCargada] = useState(false)
  const [isSuccessTareas, setIsSuccessTareas] = useState(false)
  const [isRemoverRecordatorios, setIsRemoverRecordatorios] = useState(false)

  const [guardarReportes, { isLoading: isLoadingGuardarReportes, error: errorReportes }] =
    useGuardarReportesMutation()
  const [recuperarReportes] = useRecuperarReportesPorIdRelacionadoMutation()
  const [recuperarTareas] = useRecuperarTareasPorIdRelacionadoMutation()

  const dispatch = useDispatch()
  const [arrArchivos, setArrArchivos] = useState([] as FileDropZone[])

  const [
    guardarCondicionante,
    {
      data,
      isLoading: isLoadingGuardar,
      isSuccess: isSuccessGuardar,
      isError: isErrorGuardar,
      error: errorGuardar,
    },
  ] = useGuardarCondicionanteMutation()

  const {
    data: condicionanteRecuperadaPorId,
    isSuccess,
    isError,
    error,
    isLoading,
    refetch: refetchCondicionante,
  } = useRecuperarCondicionanteporIdQuery(idSeleccionado, {
    skip: !isModoEditar,
  })

  const [
    actualizarCodicionante,
    {
      isSuccess: isSuccessActualizar,
      isLoading: isLoadingActualizar,
      isError: isErrorActualizar,
      error: errorActualizar,
    },
  ] = useActualizarCondicionanteMutation()

  const crearObjetoFormData = (formData: FormData, idRelacionado: string) => {
    // Contrato de backend para carga de evidencia documental.
    formData.append('id_relacionado', idRelacionado)
    formData.append('tipo', AppConstantes.CVE_REPORTE_TIPO_CONDICIONANTE)
    formData.append('desc_tipo', AppConstantes.DESC_REPORTE_TIPO_CONDICIONANTE)
  }

  const recorrerArregloDeArchivosCargados = (formData: FormData) => {
    // Solo adjunta archivos nuevos; los existentes no se duplican.
    arrArchivos.forEach((a) => {
      if (a.isNew && a.file) {
        formData.append('archivos', a.file, a.file.name)
      }
    })
  }

  /**
   * @description Guarda arreglo de reportes en una condicionante que ya existe.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 24/05/2023
   */
  const guardarReportesCondicionanteActualizada = () => {
    // Flujo "actualizar": primero se actualiza la condicionante, luego (si hay archivos nuevos)
    // se intenta subir evidencia. Si falla evidencia, se notifica y se deja el modal en tab reportes.
    setIsSuccessActualizar(isSuccessActualizar)
    refetch()
    const formData = new FormData()
    crearObjetoFormData(formData, condicionante?.idCondicionante ?? '')
    if (arrArchivos && arrArchivos.length > 0) {
      recorrerArregloDeArchivosCargados(formData)
      guardarReportes(formData)
        .unwrap()
        .then(() => {
          recuperarReportes(condicionante?.idCondicionante)
            .unwrap()
            .then((resp: Reporte[]) => {
              dispatch(cargarReportes(resp))
              setArrArchivos([])

              toast.success('¡Condicionante actualizada!', {
                style,
              })
              refetch()
              refetchCondicionante()
            })
            .catch(() => console.error)
        })
        .catch(() => {
          setTabActivo(AppConstantes.CVE_TAB_REPORTES)
          abrirSwalFire(
            AppConstantes.DESC_EXITOACTUALIZAR_CONDICIONANTE,
            'La condicionante se encuentra actualizada, pero algunos archivos de la evidencia documental no se pudieron guardar. Verifíquelos y actualice la condicionante.',
            true
          )
          refetchCondicionante()
          refetch()
        })
    } else {
      toast.success('Condicionante actualizada', { style })
      refetchCondicionante()
      refetch()
    }
  }

  /**
   * @description Guarda reportes en una condicionante recien creada.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 24/05/2023
   */
  const guardarReportesCondicionanteGuardada = () => {
    // Flujo "guardar": backend regresa `idCondicionante`; con ese id se suben archivos.
    setIsSuccessGuardar(isSuccessGuardar)
    refetch()
    const formData = new FormData()
    crearObjetoFormData(formData, data.idCondicionante)
    if (arrArchivos && arrArchivos.length > 0) {
      recorrerArregloDeArchivosCargados(formData)
      guardarReportes(formData)
        .unwrap()
        .then(() => {
          abrirSwalFire(
            AppConstantes.DESC_EXITOGUARDAR_CONDICIONANTE,
            'La condicionante se encuentra registrada.',
            false
          )
        })
        .catch(() => {
          if (!isLoadingGuardar || !isLoadingActualizar) {
            abrirSwalFire(
              AppConstantes.DESC_EXITOGUARDAR_CONDICIONANTE,
              'La condicionante se encuentra registrada, pero los archivos de la evidencia documental no se pudieron guardar. Verifíquelos y actualice la condicionante.',
              true
            )
          }
        })
    } else {
      abrirSwalFire(
        AppConstantes.DESC_EXITOGUARDARACTUALIZAR_CONDICIONANTE_SINREPORTES,
        'La condicionante se encuentra registrada.',
        false
      )
    }
  }

  useEffect(() => {
    if (!isModoEditar) {
      if (!fechaVencimientoAutorizacion) {
        setValue('cantidadPeriodos', 1)
      }
    }
    if (isSuccess) {
      // Cada vez que se recupera la condicionante, se hidratan reportes y tareas vinculadas.
      recuperarReportes(condicionante?.idCondicionante)
        .unwrap()
        .then((resp: Reporte[]) => {
          dispatch(cargarReportes(resp))
        })
        .catch(console.error)

      recuperarTareas(`condicionante/${condicionante?.idCondicionante}`)
        .unwrap()
        .then((resp: Tarea[]) => {
          dispatch(cargarTareas(resp))
        })
        .catch(console.error)
    }
    if (isSuccessActualizar) {
      guardarReportesCondicionanteActualizada()
    } else if (!condicionanteCargada && isModoEditarModal && isSuccess) {
      // Hidrata el formulario desde el detalle recuperado.
      // Normaliza: catálogos (estatus/periodicidad), fechas para datepicker y recordatorios a Opcion.
      const ObjetoCondicionante = {
        ...condicionanteRecuperadaPorId,
        estatus: {
          valor: condicionanteRecuperadaPorId.estatus,
          etiqueta: condicionanteRecuperadaPorId.descEstatus,
        },
        periodicidad: {
          valor: condicionanteRecuperadaPorId.periodicidad,
          etiqueta: condicionanteRecuperadaPorId.descPeriodicidad,
        },
        tareas: tareasRecuperadas?.tareas?.map((t: any) => {
          return {
            ...t,
            idTarea: t.idTarea,
            titulo: t.titulo,
            idUsuarioAsignado: {
              etiqueta: t.nombreUsuarioAsignado,
              valor: t.idUsuarioAsignado,
            },
            descripcion: t.descripcion,
            estatus: {
              valor: t.estatus,
              etiqueta: t.descEstatus,
            },
            fechaInicio: dayjs(t.fechaInicio).format(AppConstantes.FORMATO_FECHA_FIREFOX_CHROME),
            fechaTermino: dayjs(t.fechaTermino).format(AppConstantes.FORMATO_FECHA_FIREFOX_CHROME),
            recordatorios: t.recordatorios?.map((r: Recordatorio) => {
              return {
                ...r,
                cantidad: r.cantidad,
                unidad: {
                  etiqueta: r.descUnidad,
                  valor: r.unidad,
                },
              }
            }),
          }
        }),
      }
      reset(ObjetoCondicionante)
      setIsSuccessTareas(true)
      setCondicionanteCargada(true)
      refetchCondicionante()
    }

    if (isSuccessGuardar) {
      guardarReportesCondicionanteGuardada()
    }
    if (isErrorGuardar || isErrorActualizar) {
      alertUsuarios(isModoEditar, handleSubmit, errorActualizar, errorGuardar)
    }
    if (!isModoEditar) {
      // Defaults en creacion.
      setValue('estatus', {
        valor: AppConstantes.CVE_ESTATUS_CONDICIONANTE_EN_PROCESO,
        etiqueta: AppConstantes.DESC_ESTATUS_CONDICIONANTE_EN_PROCESO,
      })
      dispatch(cargarReportes([]))
    }
  }, [
    isErrorGuardar,
    isSuccessGuardar,
    isSuccessActualizar,
    isModoEditar,
    isErrorActualizar,
    isError,
    isSuccess,
  ])

  /**
   * @description Cerrar modal condicionante cuando el swalFire es confirmado
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 10/05/2023
   * @param result js
   */
  const cerrarModalResultConfirmed = (result: SweetAlertResult) => {
    result.isConfirmed && cerrarModal()
  }

  /**
   * @description Muesta el swalFire para diferentes casos de éxito o error
   * al guardar o actualizar una autorización
   *
   * @author ana.carrasco Hábil MX
   * @version 1.0.0, 13/01/2023
   * @param tipo Tipo de error: ExitoGuardarAutorizacion o ErrorGuardarActualizarAutorizacion
   * @param mensaje Mensaje que aparece en el cuerpo del swalFire
   * @param isErrorGuardarReportes Verdadero en caso de que hay un error al guardar reporte, y
   * falso cuando se guarden con exito.
   * @param err Mensaje de error al guardar o actualizar una autorizacion
   */
  const abrirSwalFire = (tipo: string, mensaje: string, isErrorGuardarReportes: boolean) => {
    const textoCondicionanteActualizada = '¡Condicionante actualizada!'
    const textoCondicionanteGuardada = '¡Condicionante guardada!'
    const textoEntendido = 'Entendido'
    if (tipo === AppConstantes.DESC_EXITOACTUALIZAR_CONDICIONANTE && isErrorGuardarReportes) {
      Swal.fire({
        title: textoCondicionanteActualizada,
        icon: 'success',
        text: mensaje,
        confirmButtonText: textoEntendido,
        allowOutsideClick: true,
        allowEscapeKey: true,
      }).then(() => {})
    }
    if (tipo === AppConstantes.DESC_EXITOGUARDAR_CONDICIONANTE && !isErrorGuardarReportes) {
      Swal.fire({
        title: textoCondicionanteGuardada,
        icon: 'success',
        text: mensaje,
        confirmButtonText: textoEntendido,
        allowOutsideClick: true,
        allowEscapeKey: true,
      }).then((result) => {
        cerrarModalResultConfirmed(result)
      })
    }
    if (tipo === AppConstantes.DESC_EXITOGUARDAR_CONDICIONANTE && isErrorGuardarReportes) {
      Swal.fire({
        title: textoCondicionanteGuardada,
        icon: 'success',
        text: mensaje,
        confirmButtonText: textoEntendido,
        allowOutsideClick: true,
        allowEscapeKey: true,
      }).then((result) => {
        cerrarModalResultConfirmed(result)
      })
    }
    if (tipo === AppConstantes.DESC_EXITOGUARDARACTUALIZAR_CONDICIONANTE_SINREPORTES) {
      Swal.fire({
        title: textoCondicionanteGuardada,
        icon: 'success',
        text: mensaje,
        confirmButtonText: textoEntendido,
        allowOutsideClick: true,
        allowEscapeKey: true,
      }).then((result) => {
        cerrarModalResultConfirmed(result)
      })
    }

    if (tipo === AppConstantes.DESC_ERRORGUARDARACTUALIZAR_CONDICIONANTE) {
      const err: any = isModoEditar ? errorActualizar : errorGuardar
      Swal.fire({
        title: 'Algo salió mal',
        icon: 'error',
        html: `Por favor valida la información o intentalo mas tarde <br/><p class='mt-5 fs-7'>(${err.status}) ${mensaje}</p>`,
        showCancelButton: true,
        confirmButtonText: 'Intentar de nuevo',
        cancelButtonText: 'Cancelar',
        allowOutsideClick: true,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          handleSubmit(onSubmit)()
          refetch()
        }
      })
    }
  }

  const onSubmit: SubmitHandler<TCondicionanteForm> = (data: TCondicionanteForm) => {
    // Mapea formulario (Opcion y fechas) a shape plano esperado por backend.
    const condicionante = {
      ...data,
      idAutorizacion: idAutorizacion,
      nombre: data.nombre,
      descripcion: data.descripcion,
      estatus: data.estatus.valor,
      descEstatus: data.estatus.etiqueta,
      periodicidad: data.periodicidad.valor,
      descPeriodicidad: data.periodicidad.etiqueta,
    }

    if (!isModoEditar) {
      // Recordatorios recurrentes solo aplican en creacion (backend los gestiona por lote).
      condicionante.recordatoriosRecurrentes =
        data.tareas.length > 0
          ? data.recordatoriosRecurrentes?.map((r: any) => {
              return {
                ...r,
                cantidad: r.cantidad,
                unidad: r.unidad?.valor,
                descUnidad: r.unidad?.etiqueta,
                fechaRecordatorio: r.fechaRecordatorio
                  ? dayjs(r.fechaRecordatorio).format(AppConstantes.FORMATO_FECHA_RECORDATORIO_POST)
                  : null,
              }
            })
          : []

      condicionante.tareas = data.tareas?.map((t: any) => {
        return {
          ...t,
          titulo: t.titulo,
          nombreUsuarioAsignado: t.idUsuarioAsignado.etiqueta,
          idUsuarioAsignado: t.idUsuarioAsignado.valor,
          fechaInicio: dayjs(t.fechaInicio).format(AppConstantes.FORMATO_FECHA_POST),
          fechaTermino: dayjs(t.fechaTermino).format(AppConstantes.FORMATO_FECHA_POST),
          descripcion: t.descripcion,
          estatus: t.estatus.valor,
          descEstatus: t.estatus.etiqueta,
          recordatorios: t.recordatorios?.map((r: TRecordatorioForm) => {
            return {
              ...r,
              cantidad: r.cantidad,
              unidad: r.unidad?.valor,
              descUnidad: r.unidad?.etiqueta,
              fechaRecordatorio: r.fechaRecordatorio
                ? dayjs(r.fechaRecordatorio).format(AppConstantes.FORMATO_FECHA_RECORDATORIO_POST)
                : null,
            }
          }),
        }
      })
    }

    if (isModoEditar) {
      // En edicion se actualiza solo el objeto condicionante (sin regenerar tareas/recordatorios por lote).
      actualizarCodicionante(condicionante)
        .unwrap()
        .then(() => {
          refetch()
        })
    } else {
      // En creacion, el backend crea condicionante + tareas + recordatorios (incluyendo recurrentes).
      guardarCondicionante(condicionante)
        .unwrap()
        .then((condicionante: Condicionante) => {
          recuperarTareas(`condicionante/${condicionante.idCondicionante}`).unwrap().then().catch()
        })
    }
  }

  /**
   * @description Cierra la ventana modal reseteando todos los campos
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 04/11/2022
   */
  const cerrarModal = () => {
    reset()
    remove()
    resetField('tareas')
    close()
    dispatch(cargarReportes([]))
    if (isModoEditar && isSuccessActualizar) {
      refetchCondicionante()
      refetch()
    }
  }
  const [tabActivo, setTabActivo] = useState(AppConstantes.CVE_TAB_TAREAS)

  /**
   * @description Cuando envias el formulario te abre el panel con el error para poder
   *              completar los campos con error y/o vacíos
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 31/03/2023
   */
  const focusErrorForm = async () => {
    const [tareasValido, recordatoriosValido] = await Promise.all([
      trigger('tareas'),
      trigger('recordatoriosRecurrentes'),
    ])

    if (fields.length === 0) {
      setIsRemoverRecordatorios(true)
    }

    if (!tareasValido || (!recordatoriosValido && fields.length !== 0)) {
      setTabActivo(
        !tareasValido
          ? AppConstantes.CVE_TAB_TAREAS
          : AppConstantes.CVE_TAB_RECORDATORIOS_RECURRENTES
      )

      toast.error('Llene todos los campos correctamente.', {
        style,
      })
    }

    if (errorReportes) {
      setTabActivo(AppConstantes.CVE_TAB_REPORTES)
    }
  }
  return (
    <>
      <Toaster />
      <Modal
        show={show}
        size={undefined}
        onHide={cerrarModal}
        dialogClassName='modal-condicionante'
        keyboard={false}
        backdrop='static'>
        <>
          {isModoEditar && (isLoading || isError) ? (
            <CargaInfo
              mensaje={'Recuperando datos de la condicionante'}
              isError={isError}
              mensajeError={'Ocurrió un error al recuperar los datos de la condicionante'}
              errorApi={error}
            />
          ) : (
            <>
              <ModalCondicionanteHeader
                condicionanteRecuperadaPorId={condicionanteRecuperadaPorId}
                titulo={titulo}
                cerrarModal={cerrarModal}></ModalCondicionanteHeader>

              {(!isModoEditar || (isModoEditar && isSuccess)) && (
                <>
                  <ModalCondicionanteBody
                    isModoEditar={isModoEditar}
                    isLoadingGuardar={isLoadingGuardar}
                    isLoadingActualizar={isLoadingActualizar}
                    isLoadingGuardarReportes={isLoadingGuardarReportes}
                    usuarioPermiso={usuarioPermiso}
                    fechaVencimientoAutorizacion={fechaVencimientoAutorizacion}
                    condicionante={condicionanteRecuperadaPorId}
                    tabActivo={tabActivo}
                    setTabActivo={setTabActivo}
                    arrArchivos={arrArchivos}
                    setArrArchivos={setArrArchivos}
                    idSitio={idSitio}
                    idAutorizacion={idAutorizacion}
                    fields={fields}
                    remove={remove}
                    append={append}
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    isRemoverRecordatorios={isRemoverRecordatorios}
                    {...{
                      control,
                      register,
                      errors,
                      getValues,
                      watch,
                      setValue,
                      trigger,
                      reset,
                    }}
                    isSuccessTareas={isSuccessTareas}></ModalCondicionanteBody>
                  <ModalCondicionanteFooter
                    isLoadingActualizar={isLoadingActualizar}
                    isLoadingGuardar={isLoadingGuardar}
                    isLoadingGuardarReportes={isLoadingGuardarReportes}
                    usuarioPermiso={usuarioPermiso}
                    isModoEditar={isModoEditar}
                    cerrarModal={cerrarModal}
                    focusErrorForm={focusErrorForm}></ModalCondicionanteFooter>
                </>
              )}
            </>
          )}
        </>
      </Modal>
    </>
  )
}
